import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useId, type ComponentPropsWithRef, type ReactNode } from 'react'

import { FOCUS_OUTLINE_ON_WRAPPER } from './focusRing.ts'

export type InputSize = 'small' | 'medium'

export interface InputProps extends Omit<ComponentPropsWithRef<'input'>, 'size'> {
  size?: InputSize
  label?: ReactNode
  helperText?: ReactNode
  errorMessage?: ReactNode
  leadingIcon?: IconDefinition
  trailingIcon?: IconDefinition
  prefixAdornment?: ReactNode
  suffixAdornment?: ReactNode
  trailingAction?: ReactNode
  containerClassName?: string
  fieldClassName?: string
}

const SIZE_CLASSES: Record<InputSize, string> = {
  small: 'h-8',
  medium: 'h-10',
}

function joinClasses(...classes: Array<string | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

/**
 * Reusable Quave Run text input matching Figma ingredients 250:5022 and 4855:864.
 */
export function Input({
  label,
  helperText,
  errorMessage,
  leadingIcon,
  trailingIcon,
  prefixAdornment,
  suffixAdornment,
  trailingAction,
  containerClassName,
  fieldClassName,
  className,
  id,
  size = 'medium',
  disabled = false,
  required = false,
  'aria-describedby': providedDescribedBy,
  'aria-invalid': providedInvalid,
  ref,
  ...rest
}: InputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const description = errorMessage ?? helperText
  const descriptionId = description ? `${inputId}-description` : undefined
  const describedBy = [providedDescribedBy, descriptionId].filter(Boolean).join(' ') || undefined
  const invalid =
    errorMessage !== undefined ||
    (providedInvalid !== undefined && providedInvalid !== false && providedInvalid !== 'false')
  const ariaInvalid = errorMessage !== undefined ? true : providedInvalid
  const status = disabled ? 'disabled' : invalid ? 'error' : 'default'
  const contentColor = disabled ? 'text-text-disabled' : 'text-text-weak'
  const iconColor = disabled ? 'text-icon-disabled' : 'text-icon-neutral'

  return (
    <div className={joinClasses('flex w-full flex-col items-start gap-1', containerClassName)}>
      {label ? (
        <label htmlFor={inputId} className="text-tP flex items-start gap-1 text-text-strong">
          <span>{label}</span>
          {required ? (
            <span aria-hidden="true" className="text-text-danger">
              *
            </span>
          ) : null}
        </label>
      ) : null}

      <div
        data-status={status}
        data-size={size}
        className={joinClasses(
          'flex w-full items-center gap-2 rounded-[var(--radius-base)] bg-fill-white px-3 transition-[background-color,border-color] duration-150',
          SIZE_CLASSES[size],
          disabled
            ? 'cursor-not-allowed border border-stroke-disabled'
            : invalid
              ? 'border-2 border-stroke-error-strong hover:bg-fill-hover has-[input:active]:bg-fill-press'
              : 'border border-stroke-strong hover:bg-fill-hover focus-within:border-stroke-selected has-[input:active]:bg-fill-press',
          fieldClassName,
        )}
      >
        {leadingIcon ? (
          <span
            aria-hidden="true"
            data-slot="input-leading-icon"
            className={joinClasses('inline-flex shrink-0 items-center justify-center', iconColor)}
          >
            <FontAwesomeIcon icon={leadingIcon} className="size-4" />
          </span>
        ) : null}

        {prefixAdornment ? (
          <span
            aria-hidden="true"
            data-slot="input-prefix"
            className={joinClasses('text-tP shrink-0', contentColor)}
          >
            {prefixAdornment}
          </span>
        ) : null}

        <input
          {...rest}
          ref={ref}
          id={inputId}
          disabled={disabled}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          className={joinClasses(
            // This field's focus signal is the WRAPPER's border above
            // (`focus-within:border-stroke-selected`), so here we only suppress
            // the default ring -- via the constant, which keeps high contrast.
            `text-tP min-w-0 flex-1 bg-transparent text-text-strong ${FOCUS_OUTLINE_ON_WRAPPER} placeholder:text-text-weak disabled:cursor-not-allowed disabled:text-text-disabled disabled:placeholder:text-text-disabled`,
            className,
          )}
        />

        {suffixAdornment ? (
          <span
            aria-hidden="true"
            data-slot="input-suffix"
            className={joinClasses('text-tP shrink-0', contentColor)}
          >
            {suffixAdornment}
          </span>
        ) : null}

        {trailingIcon ? (
          <span
            aria-hidden="true"
            data-slot="input-trailing-icon"
            className={joinClasses('inline-flex shrink-0 items-center justify-center', iconColor)}
          >
            <FontAwesomeIcon icon={trailingIcon} className="size-4" />
          </span>
        ) : null}

        {trailingAction ? (
          <span
            data-slot="input-trailing-action"
            className={joinClasses('inline-flex shrink-0 items-center [&_svg]:size-4', iconColor)}
          >
            {trailingAction}
          </span>
        ) : null}
      </div>

      {description ? (
        <p
          id={descriptionId}
          className={joinClasses(
            'text-tSmall w-full',
            invalid ? 'text-text-danger' : 'text-text-weak',
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  )
}
